import React, { useState, useEffect } from 'react';
import { FaGoogle, FaFacebook } from 'react-icons/fa';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, EmailRounded, LockRounded } from '@mui/icons-material';
import '../styles/global.css';
import '../styles/RegisterPage.css';
import { toFriendlyMessage } from '../utils/errorUtils';
import { isTouchDevice } from '../utils/device';
import { useAuth } from '../components/AuthContext';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useTranslation } from 'react-i18next';

/* Floating decoration bubble */
const FloatingShape = ({ size, top, left, delay, duration, color }) => (
  <Box sx={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, opacity: 0.15, top, left,
    animation: `loginFloat ${duration}s ${delay}s ease-in-out infinite`,
    pointerEvents: 'none'
  }} />
);

export default function LoginPage() {
  const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;
  const { login } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  // Prevent aggressive browser/password-manager autofill by keeping inputs readOnly until user interacts
  const [emailRo, setEmailRo] = useState(true);
  const [passwordRo, setPasswordRo] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const getAdminPortalUrl = () => {
    try {
      const api = process.env.REACT_APP_API_BASE_URL || '';
      const base = api.replace(/\/$/, '').replace(/\/api$/, '').replace(/\/api\/$/, '');
      return `${base}/admin-portal`;
    } catch {
      return '/admin-portal';
    }
  };

  // Check for redirect message from protected route
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    // Client-side validation using MUI inline errors
    const errs = {};
    if (!emailRegex.test(email)) errs.email = t('auth.invalidEmail');
    if ((password || '').length < 6) errs.password = t('auth.passwordMin');
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setError(t('auth.checkRequired'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      // Check content type to handle non-JSON responses
      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        // Handle error based on content type
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('auth.loginFailed'));
        } else {
          // Handle non-JSON error response (like HTML)
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error(t('auth.serverError'));
        }
      }

      // Only parse JSON if content type is application/json
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('auth.invalidResponse'));
      }

      const data = await response.json();

      // Use the login function from AuthContext, pass remember preference
      login(data.user, data.token, remember);

      // If admin or teacher, go to the Admin Create UI within the SPA
      {
        const role = data?.user?.role ? String(data.user.role).toLowerCase() : undefined;
        const roles = Array.isArray(data?.user?.roles)
          ? data.user.roles.map(r => String(r).toLowerCase())
          : (role ? [role] : []);
        if (roles.includes('admin') || roles.includes('teacher')) {
          navigate('/admin/create');
          return;
        }
      }
      // Otherwise redirect to the previous page or home page
      if (location.state?.from) {
        navigate(location.state.from);
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  /* Shared input style */
  const inputPropsSx = { borderRadius: '14px', fontSize: '1rem' };

  return (
    <>
      <style>{`
        @keyframes loginFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes loginGradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes loginSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Full-screen animated gradient background */}
      <Box sx={{
        minHeight: 'calc(100vh - 60px)',
        mt: '-1.25em',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 35%, #f093fb 70%, #fc5c7d 100%)',
        backgroundSize: '300% 300%',
        animation: 'loginGradientShift 10s ease infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
        py: { xs: 3, md: 5 }, px: 2
      }}>
        {/* Floating background shapes */}
        <FloatingShape size={180} top="3%" left="-2%" delay={0} duration={7} color="#fff" />
        <FloatingShape size={110} top="60%" left="5%" delay={1.2} duration={5.5} color="#667eea" />
        <FloatingShape size={80} top="10%" left="82%" delay={0.6} duration={6} color="#f093fb" />
        <FloatingShape size={140} top="72%" left="76%" delay={2} duration={6.5} color="#fff" />
        <FloatingShape size={65} top="35%" left="90%" delay={1} duration={5} color="#764ba2" />
        <FloatingShape size={95} top="82%" left="42%" delay={0.3} duration={7.5} color="#fc5c7d" />

        {/* Main card */}
        <Box sx={{
          width: '100%', maxWidth: 480,
          borderRadius: '28px',
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(24px)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
          position: 'relative', zIndex: 1,
          overflow: 'hidden'
        }}>

          {/* ===== Fun header banner inside the card ===== */}
          <Box sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 3, sm: 4 },
            textAlign: 'center', position: 'relative', overflow: 'hidden'
          }}>
            {/* Decorative circles in banner */}
            <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />

            {/* Emoji ring */}
            <Box sx={{
              display: 'inline-flex', position: 'relative',
              width: { xs: 100, sm: 120 }, height: { xs: 100, sm: 120 },
              alignItems: 'center', justifyContent: 'center', mb: 2
            }}>
              {/* Spinning ring */}
              <Box sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                border: '3px dashed rgba(255,255,255,0.3)',
                animation: 'loginSpin 20s linear infinite'
              }} />
              {/* Central emoji */}
              <Box sx={{
                fontSize: { xs: '3.5rem', sm: '4.2rem' }, lineHeight: 1,
                animation: 'loginFloat 3s ease-in-out infinite',
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))'
              }}>
                🚀
              </Box>
              {/* Corner emojis */}
              <Box sx={{ position: 'absolute', top: -4, left: -4, fontSize: '1.4rem', animation: 'loginFloat 4s 0.2s ease-in-out infinite' }}>⭐</Box>
              <Box sx={{ position: 'absolute', top: -4, right: -4, fontSize: '1.4rem', animation: 'loginFloat 3.5s 0.8s ease-in-out infinite' }}>📚</Box>
              <Box sx={{ position: 'absolute', bottom: -4, left: -4, fontSize: '1.4rem', animation: 'loginFloat 4.5s 0.5s ease-in-out infinite' }}>💡</Box>
              <Box sx={{ position: 'absolute', bottom: -4, right: -4, fontSize: '1.4rem', animation: 'loginFloat 3.8s 1s ease-in-out infinite' }}>🎯</Box>
            </Box>

            <Typography variant="h4" fontWeight={900}
              sx={{ color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.15)',
                fontSize: { xs: '1.7rem', sm: '2.1rem' }, letterSpacing: 0.5 }}>
              {t('auth.welcomeBack')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5, fontWeight: 500,
              fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              {t('auth.letsLearn')}
            </Typography>
          </Box>

          {/* ===== Form area ===== */}
          <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 3, sm: 4 } }}>

            {/* Alerts */}
            {!!error && (
              <Alert severity="error" onClose={() => setError(null)}
                sx={{ mb: 2, borderRadius: '14px', fontSize: '0.95rem' }}>
                {toFriendlyMessage(error)}
              </Alert>
            )}
            {!!message && !error && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: '14px', fontSize: '0.95rem' }}>
                {message}
              </Alert>
            )}

            {/* Login form */}
            <Box component="form" onSubmit={handleLogin} noValidate autoComplete="off">
              <TextField
                fullWidth
                type="email"
                id="email"
                label={t('auth.email')}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: emailRegex.test(e.target.value) ? '' : t('auth.invalidEmail') })); }}
                onBlur={(e) => setFieldErrors(prev => ({ ...prev, email: emailRegex.test(e.target.value) ? '' : t('auth.invalidEmail') }))}
                onFocus={() => setEmailRo(false)}
                placeholder="ví dụ: tenban@gmail.com"
                inputProps={{ inputMode: 'email', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: 'false', readOnly: emailRo }}
                autoFocus={!isTouchDevice()}
                required
                disabled={loading}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || t('auth.emailHelper')}
                sx={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRounded sx={{ color: '#667eea', fontSize: 22 }} />
                    </InputAdornment>
                  ),
                  sx: inputPropsSx
                }}
              />

              <TextField
                fullWidth
                id="password"
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setFieldErrors(prev => ({ ...prev, password: (e.target.value || '').length >= 6 ? '' : t('auth.passwordMin') })); }}
                onBlur={(e) => setFieldErrors(prev => ({ ...prev, password: (e.target.value || '').length >= 6 ? '' : t('auth.passwordMin') }))}
                onFocus={() => setPasswordRo(false)}
                placeholder="••••••••"
                required
                disabled={loading}
                inputProps={{ minLength: 6, 'aria-label': t('auth.password'), readOnly: passwordRo }}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || t('auth.passwordHelper')}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded sx={{ color: '#764ba2', fontSize: 22 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                        title={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                        onClick={() => setShowPassword(v => !v)}
                        edge="end" size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: inputPropsSx
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={loading} size="small" />}
                  label={<Typography variant="body2">{t('auth.remember')}</Typography>}
                />
                <Link component={RouterLink} to="/forgot-password" underline="hover"
                  sx={{ fontSize: '0.85rem', color: '#764ba2' }}>
                  {t('auth.forgotPassword')}
                </Link>
              </Box>

              {/* Gradient login button */}
              <Button
                type="submit"
                fullWidth
                disabled={loading}
                aria-label={loading ? t('auth.loggingIn') : t('auth.login')}
                startIcon={loading ? <CircularProgress color="inherit" size={20} /> : null}
                sx={{
                  height: 54, borderRadius: '14px', fontSize: '1.1rem', fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: '#fff', textTransform: 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 20px rgba(102,126,234,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd6, #6a4199)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 10px 30px rgba(102,126,234,0.45)'
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'rgba(255,255,255,0.7)'
                  }
                }}
              >
                {loading ? t('auth.loggingIn') : t('auth.login')}
              </Button>

              <Divider sx={{ my: 2.5 }}>{t('common.or')}</Divider>

              {/* Social buttons */}
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined"
                    startIcon={<FaGoogle aria-hidden="true" />}
                    onClick={() => setComingSoonOpen(true)}
                    sx={{ borderRadius: '14px', textTransform: 'none', height: 46, fontSize: '0.9rem' }}>
                    Google
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth variant="outlined"
                    startIcon={<FaFacebook aria-hidden="true" />}
                    onClick={() => setComingSoonOpen(true)}
                    sx={{ borderRadius: '14px', textTransform: 'none', height: 46, fontSize: '0.9rem' }}>
                    Facebook
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <ConfirmationDialog
              isOpen={comingSoonOpen}
              title={t('register.dialog.comingSoonTitle', 'Coming soon')}
              message={t('register.dialog.comingSoonMessage', 'This feature is coming soon. Please check back later.')}
              cancelText={t('register.dialog.close', 'Close')}
              onCancel={() => setComingSoonOpen(false)}
            />

            {/* Register CTA */}
            <Box sx={{
              mt: 3, p: 2, borderRadius: '14px',
              background: 'linear-gradient(135deg, rgba(102,126,234,0.08), rgba(240,147,251,0.08))',
              border: '2px dashed rgba(102,126,234,0.25)', textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                {t('auth.newHere')}{' '}
                <Link component={RouterLink} to="/register" underline="hover" fontWeight={700}
                  sx={{ color: '#764ba2' }}>
                  {t('auth.createAccount')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
